import React, { useState, useEffect, useMemo } from "react";
import { View, StyleSheet, ScrollView, TouchableOpacity } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { useTheme } from "@/hooks/useTheme";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { useEntityEdit } from "@/contexts/EntityEditContext";
import { ProjectType } from "@/types/db/projects";
import { tenantService } from "@/services/tenantService";
import { getTermFieldMetadata } from "@/types/schemas/terms";
import { SkeletonRow } from "@/components/ui/Skeleton";

export interface ProjectTermsAdjudicationTabProps {
  project: ProjectType | null;
  loading?: boolean;
}

type TermSource = "tenant" | "client" | "project";

interface TermComparison {
  fieldKey: string;
  description: string;
  type: "number" | "boolean" | "string" | "enum";
  enumValues?: string[];
  split?: boolean;
  status: "match" | "conflict" | "partial";
  sources: {
    tenant?: any;
    client?: any;
    project?: any;
  };
  selectedSource?: TermSource;
  selectedBillableSource?: TermSource;
  selectedPayableSource?: TermSource;
}

export function ProjectTermsAdjudicationTab({
  project,
  loading: projectLoading = false,
}: ProjectTermsAdjudicationTabProps) {
  const { colors } = useTheme();
  const { editing, formData, updateFormData } = useEntityEdit<ProjectType>();
  const [tenantTerms, setTenantTerms] = useState<Record<string, any> | null>(null);
  const [loading, setLoading] = useState(true);
  const [comparisons, setComparisons] = useState<TermComparison[]>([]);

  const TERM_FIELDS = useMemo(() => getTermFieldMetadata(), []);

  // Fetch tenant terms on mount
  useEffect(() => {
    const fetchTenantTerms = async () => {
      try {
        setLoading(true);
        const metaData = await tenantService.getTenantMetaData();
        setTenantTerms(metaData?.terms || {});
      } catch (error) {
        console.error("Failed to fetch tenant terms:", error);
        setTenantTerms({});
      } finally {
        setLoading(false);
      }
    };

    fetchTenantTerms();
  }, []);

  // Compare terms from all sources
  useEffect(() => {
    if (!tenantTerms || !project) return;

    const clientTerms = project.client?.meta_data?.terms || {};
    const projectTerms = editing ? formData?.meta_data?.terms || {} : project.meta_data?.terms || {};

    // Get all unique field keys from all sources
    const allFieldKeys = new Set([
      ...Object.keys(tenantTerms),
      ...Object.keys(clientTerms),
      ...Object.keys(projectTerms),
    ]);

    const newComparisons: TermComparison[] = [];

    allFieldKeys.forEach((fieldKey) => {
      const fieldMeta = TERM_FIELDS[fieldKey];
      if (!fieldMeta) return;

      const tenantValue = tenantTerms[fieldKey];
      const clientValue = clientTerms[fieldKey];
      let projectValue = projectTerms[fieldKey];

      const isSplitField = fieldMeta.split || false;

      // Handle split fields differently
      if (isSplitField) {
        // Check if project has split adjudicated term: { billable: { value, source }, payable: { value, source } }
        let billableAdjudicatedSource: TermSource | undefined;
        let payableAdjudicatedSource: TermSource | undefined;

        if (
          projectValue &&
          typeof projectValue === "object" &&
          "billable" in projectValue &&
          "payable" in projectValue
        ) {
          const billableData = projectValue.billable;
          const payableData = projectValue.payable;

          if (billableData && typeof billableData === "object" && "source" in billableData) {
            billableAdjudicatedSource = billableData.source as TermSource;
          }
          if (payableData && typeof payableData === "object" && "source" in payableData) {
            payableAdjudicatedSource = payableData.source as TermSource;
          }
        }

        // Build sources object
        const sources: Record<string, any> = {};
        if (tenantValue !== undefined) sources.tenant = tenantValue;
        if (clientValue !== undefined) sources.client = clientValue;
        // Don't show "project" as a source for adjudicated split fields
        if (!billableAdjudicatedSource && !payableAdjudicatedSource && projectValue !== undefined) {
          sources.project = projectValue;
        }

        const sourceCount = Object.keys(sources).length;
        if (sourceCount === 0) return; // No sources have this field

        // Determine status for split fields
        let status: "match" | "conflict" | "partial" = "partial";
        let selectedSource: TermSource | undefined;
        let selectedBillableSource: TermSource | undefined;
        let selectedPayableSource: TermSource | undefined;

        if (billableAdjudicatedSource && payableAdjudicatedSource) {
          // Already adjudicated - restore selections
          selectedBillableSource = billableAdjudicatedSource;
          selectedPayableSource = payableAdjudicatedSource;

          // Determine status based on whether source values actually match
          if (sourceCount === 1) {
            status = "partial";
          } else {
            const values = Object.values(sources);
            const allMatch = values.every((v) => JSON.stringify(v) === JSON.stringify(values[0]));
            status = allMatch ? "match" : "conflict";
          }
        } else if (sourceCount === 1) {
          status = "partial";
          const source = Object.keys(sources)[0] as TermSource;
          selectedSource = source; // For rendering single-row UI
          selectedBillableSource = source;
          selectedPayableSource = source;
        } else {
          // Multiple sources - check if they match
          const values = Object.values(sources);
          const allMatch = values.every((v) => JSON.stringify(v) === JSON.stringify(values[0]));

          if (allMatch) {
            status = "match";
            const source = Object.keys(sources)[0] as TermSource;
            selectedSource = source; // For rendering single-row UI when split field matches
            selectedBillableSource = source;
            selectedPayableSource = source;
          } else {
            status = "conflict";
            // Default selections
            selectedBillableSource = clientValue !== undefined ? "client" : "tenant";
            selectedPayableSource = tenantValue !== undefined ? "tenant" : "client";
          }
        }

        newComparisons.push({
          fieldKey,
          description: fieldMeta.description,
          type: fieldMeta.type,
          enumValues: fieldMeta.enumValues,
          split: true,
          status,
          sources,
          selectedSource,
          selectedBillableSource,
          selectedPayableSource,
        });
      } else {
        // Non-split field logic (original)
        let projectAdjudicatedSource: TermSource | undefined;
        if (projectValue && typeof projectValue === "object" && "value" in projectValue && "source" in projectValue) {
          projectAdjudicatedSource = projectValue.source as TermSource;
          projectValue = projectValue.value; // Extract actual value
        }

        // Build sources object
        const sources: Record<string, any> = {};
        if (tenantValue !== undefined) sources.tenant = tenantValue;
        if (clientValue !== undefined) sources.client = clientValue;
        if (!projectAdjudicatedSource && projectValue !== undefined) {
          sources.project = projectValue;
        }

        const sourceCount = Object.keys(sources).length;
        if (sourceCount === 0) return; // No sources have this field

        // Determine status
        let status: "match" | "conflict" | "partial" = "partial";
        let selectedSource: TermSource | undefined;

        if (projectAdjudicatedSource) {
          selectedSource = projectAdjudicatedSource;

          // Determine status based on whether source values actually match
          if (sourceCount === 1) {
            status = "partial";
          } else {
            const values = Object.values(sources);
            const allMatch = values.every((v) => JSON.stringify(v) === JSON.stringify(values[0]));
            status = allMatch ? "match" : "conflict";
          }
        } else if (sourceCount === 1) {
          status = "partial";
          selectedSource = Object.keys(sources)[0] as TermSource;
        } else {
          const values = Object.values(sources);
          const allMatch = values.every((v) => JSON.stringify(v) === JSON.stringify(values[0]));

          if (allMatch) {
            status = "match";
            selectedSource = Object.keys(sources)[0] as TermSource;
          } else {
            status = "conflict";
            selectedSource = clientValue !== undefined ? "client" : "tenant";
          }
        }

        newComparisons.push({
          fieldKey,
          description: fieldMeta.description,
          type: fieldMeta.type,
          enumValues: fieldMeta.enumValues,
          split: false,
          status,
          sources,
          selectedSource,
        });
      }
    });

    // Sort by status (conflict first for visibility, then match, then partial)
    newComparisons.sort((a, b) => {
      const order = { conflict: 0, match: 1, partial: 2 };
      return order[a.status] - order[b.status];
    });

    setComparisons(newComparisons);
  }, [tenantTerms, project, editing, formData, TERM_FIELDS]);

  const formatValue = (value: any, type: string): string => {
    if (value === undefined || value === null) return "—";
    if (type === "boolean") return value ? "Yes" : "No";
    return String(value);
  };

  // Auto-apply adjudicated terms to form data
  const applyAdjudicatedTerms = (updatedComparisons: TermComparison[]) => {
    if (!editing) return;

    // Build adjudicated terms from selections with source metadata
    const adjudicatedTerms: Record<string, any> = {};

    updatedComparisons.forEach((comp) => {
      if (comp.split) {
        // Handle split fields - save both billable and payable
        if (comp.selectedBillableSource && comp.selectedPayableSource) {
          const billableValue = comp.sources[comp.selectedBillableSource];
          const payableValue = comp.sources[comp.selectedPayableSource];

          if (billableValue !== undefined && payableValue !== undefined) {
            adjudicatedTerms[comp.fieldKey] = {
              billable: {
                value: billableValue,
                source: comp.selectedBillableSource,
              },
              payable: {
                value: payableValue,
                source: comp.selectedPayableSource,
              },
            };
          }
        }
      } else {
        // Handle non-split fields - original behavior
        if (comp.selectedSource && comp.sources[comp.selectedSource] !== undefined) {
          adjudicatedTerms[comp.fieldKey] = {
            value: comp.sources[comp.selectedSource],
            source: comp.selectedSource,
          };
        }
      }
    });

    updateFormData({
      ...formData,
      meta_data: {
        ...formData.meta_data,
        terms: adjudicatedTerms,
      },
    });
  };

  const handleSourceSelection = (fieldKey: string, source: TermSource) => {
    setComparisons((prev) => {
      const updated = prev.map((comp) => {
        if (comp.fieldKey === fieldKey) {
          // If it's a split field shown in single-row mode (match or partial), update billable/payable too
          if (comp.split && (comp.status === "match" || comp.status === "partial")) {
            return {
              ...comp,
              selectedSource: source,
              selectedBillableSource: source,
              selectedPayableSource: source,
            };
          }
          return { ...comp, selectedSource: source };
        }
        return comp;
      });

      // Auto-apply changes
      applyAdjudicatedTerms(updated);
      return updated;
    });
  };

  const handleBillableSourceSelection = (fieldKey: string, source: TermSource) => {
    setComparisons((prev) => {
      const updated = prev.map((comp) =>
        comp.fieldKey === fieldKey ? { ...comp, selectedBillableSource: source } : comp,
      );

      // Auto-apply changes
      applyAdjudicatedTerms(updated);
      return updated;
    });
  };

  const handlePayableSourceSelection = (fieldKey: string, source: TermSource) => {
    setComparisons((prev) => {
      const updated = prev.map((comp) =>
        comp.fieldKey === fieldKey ? { ...comp, selectedPayableSource: source } : comp,
      );

      // Auto-apply changes
      applyAdjudicatedTerms(updated);
      return updated;
    });
  };

  const renderSourceRadio = (comp: TermComparison, source: TermSource, purpose?: "billable" | "payable") => {
    const value = comp.sources[source];
    if (value === undefined) return null;

    // Disable interaction for non-conflicting terms in edit mode
    const isDisabled = editing && comp.status !== "conflict";

    // Determine if this source is selected based on purpose
    let isSelected: boolean;
    let onPressHandler: () => void;

    if (purpose === "billable") {
      isSelected = comp.selectedBillableSource === source;
      onPressHandler = () => handleBillableSourceSelection(comp.fieldKey, source);
    } else if (purpose === "payable") {
      isSelected = comp.selectedPayableSource === source;
      onPressHandler = () => handlePayableSourceSelection(comp.fieldKey, source);
    } else {
      isSelected = comp.selectedSource === source;
      onPressHandler = () => handleSourceSelection(comp.fieldKey, source);
    }

    const label = source.charAt(0).toUpperCase() + source.slice(1);
    const displayLabel = isSelected && !editing ? `Using ${label}` : label;

    if (!editing) {
      // Preview mode - show as badge
      return (
        <View
          key={source}
          style={[
            styles.sourceBadge,
            {
              backgroundColor: isSelected ? colors.primary : colors.card,
              borderColor: isSelected ? colors.primary : colors.border,
            },
          ]}
        >
          <ThemedText style={[styles.sourceLabel, isSelected && { color: "white" }]}>{displayLabel}</ThemedText>
          <ThemedText style={[{ fontWeight: "600" }, isSelected && { color: "white" }]}>
            {formatValue(value, comp.type)}
          </ThemedText>
        </View>
      );
    }

    // Edit mode - show as radio button
    return (
      <TouchableOpacity
        key={source}
        style={[styles.radioOption, { borderColor: colors.border }, isDisabled && { opacity: 0.5 }]}
        onPress={isDisabled ? undefined : onPressHandler}
        disabled={isDisabled}
      >
        <View style={[styles.radio, { borderColor: colors.primary }]}>
          {isSelected && <View style={[styles.radioInner, { backgroundColor: colors.primary }]} />}
        </View>
        <View style={styles.radioLabel}>
          <ThemedText style={styles.sourceLabel}>{label}</ThemedText>
          <ThemedText style={{ fontWeight: "600" }}>{formatValue(value, comp.type)}</ThemedText>
        </View>
      </TouchableOpacity>
    );
  };

  const renderComparison = (comp: TermComparison) => {
    const { fieldKey, description, status, split } = comp;

    // Icon and color based on status
    const statusConfig = {
      match: { icon: "check-circle" as const, color: colors.green.text, label: "Matching" },
      conflict: { icon: "alert-circle" as const, color: colors.yellow.text, label: "Conflict" },
      partial: { icon: "information" as const, color: colors.text, label: "Single Source" },
    };

    const config = statusConfig[status];

    return (
      <View
        key={fieldKey}
        style={[styles.comparisonCard, { backgroundColor: colors.card, borderColor: colors.border }]}
      >
        <View style={styles.comparisonHeader}>
          <View style={styles.comparisonTitle}>
            <MaterialCommunityIcons name={config.icon} size={20} color={config.color} />
            <ThemedText type="defaultSemiBold">{description}</ThemedText>
          </View>
          <ThemedText style={{ opacity: 0.6, fontSize: 12 }}>{config.label}</ThemedText>
        </View>

        {split && status === "conflict" ? (
          // Split field with conflicting values - show billable and payable rows
          <>
            <View style={{ marginBottom: 12 }}>
              <ThemedText style={{ fontSize: 13, fontWeight: "600", marginBottom: 8, opacity: 0.8 }}>
                For Billable (Client Charges)
              </ThemedText>
              <View style={editing ? styles.radioGroup : styles.badgeGroup}>
                {renderSourceRadio(comp, "tenant", "billable")}
                {renderSourceRadio(comp, "client", "billable")}
                {renderSourceRadio(comp, "project", "billable")}
              </View>
            </View>
            <View>
              <ThemedText style={{ fontSize: 13, fontWeight: "600", marginBottom: 8, opacity: 0.8 }}>
                For Payable (Worker Costs)
              </ThemedText>
              <View style={editing ? styles.radioGroup : styles.badgeGroup}>
                {renderSourceRadio(comp, "tenant", "payable")}
                {renderSourceRadio(comp, "client", "payable")}
                {renderSourceRadio(comp, "project", "payable")}
              </View>
            </View>
          </>
        ) : (
          // Non-split field OR split field with matching/single source - show single row
          <>
            {status === "match" && !editing ? (
              <View style={styles.matchValue}>
                <ThemedText style={{ opacity: 0.8 }}>
                  All sources agree:{" "}
                  <ThemedText style={{ fontWeight: "600" }}>
                    {formatValue(comp.sources.tenant || comp.sources.client || comp.sources.project, comp.type)}
                  </ThemedText>
                </ThemedText>
              </View>
            ) : (
              <View style={editing ? styles.radioGroup : styles.badgeGroup}>
                {renderSourceRadio(comp, "tenant")}
                {renderSourceRadio(comp, "client")}
                {renderSourceRadio(comp, "project")}
              </View>
            )}
          </>
        )}
      </View>
    );
  };

  const matchingComparisons = comparisons.filter((c) => c.status === "match");
  const conflictingComparisons = comparisons.filter((c) => c.status === "conflict");
  const partialComparisons = comparisons.filter((c) => c.status === "partial");

  if (loading || projectLoading) {
    return (
      <View style={styles.content}>
        <SkeletonRow />
        <SkeletonRow />
        <SkeletonRow />
      </View>
    );
  }

  return (
    <ScrollView style={styles.content}>
      <View style={[styles.header, { backgroundColor: colors.card, borderColor: colors.border }]}>
        <ThemedText type="subtitle">Terms {editing ? "Adjudication" : "Overview"}</ThemedText>
        <ThemedText style={{ opacity: 0.7, marginTop: 4 }}>
          {editing
            ? "Resolve conflicts between Tenant (global), Client, and Project terms. Select which source to use for each conflicting field."
            : "View term configurations from Tenant, Client, and Project sources. Matching terms are applied automatically."}
        </ThemedText>
      </View>

      {conflictingComparisons.length > 0 && (
        <View style={styles.section}>
          <View style={styles.sectionHeader}>
            <MaterialCommunityIcons name="alert-circle" size={18} color={colors.yellow.text} />
            <ThemedText type="defaultSemiBold" style={{ color: colors.yellow.text }}>
              Conflicting Terms ({conflictingComparisons.length})
            </ThemedText>
          </View>
          {conflictingComparisons.map(renderComparison)}
        </View>
      )}

      {matchingComparisons.length > 0 && (
        <View style={styles.section}>
          <View style={styles.sectionHeader}>
            <MaterialCommunityIcons name="check-circle" size={18} color={colors.green.text} />
            <ThemedText type="defaultSemiBold" style={{ color: colors.green.text }}>
              Matching Terms ({matchingComparisons.length})
            </ThemedText>
          </View>
          {matchingComparisons.map(renderComparison)}
        </View>
      )}

      {partialComparisons.length > 0 && (
        <View style={styles.section}>
          <View style={styles.sectionHeader}>
            <MaterialCommunityIcons name="information" size={18} color={colors.text} />
            <ThemedText type="defaultSemiBold">Single Source Terms ({partialComparisons.length})</ThemedText>
          </View>
          {partialComparisons.map(renderComparison)}
        </View>
      )}

      {comparisons.length === 0 && (
        <View style={[styles.emptyState, { backgroundColor: colors.card, borderColor: colors.border }]}>
          <MaterialCommunityIcons name="file-document-outline" size={48} color={colors.text} style={{ opacity: 0.3 }} />
          <ThemedText style={{ opacity: 0.6, marginTop: 8 }}>No terms configured in any source</ThemedText>
        </View>
      )}
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  content: {
    flex: 1,
  },
  header: {
    margin: 16,
    padding: 16,
    borderWidth: StyleSheet.hairlineWidth,
    borderRadius: 12,
  },
  infoBox: {
    margin: 16,
    padding: 16,
    borderWidth: StyleSheet.hairlineWidth,
    borderRadius: 12,
    flexDirection: "row",
    gap: 12,
    alignItems: "flex-start",
  },
  section: {
    marginHorizontal: 16,
    marginBottom: 24,
  },
  sectionHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    marginBottom: 12,
  },
  comparisonCard: {
    padding: 16,
    borderWidth: StyleSheet.hairlineWidth,
    borderRadius: 12,
    marginBottom: 12,
  },
  comparisonHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 12,
  },
  comparisonTitle: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  matchValue: {
    paddingVertical: 8,
  },
  radioGroup: {
    gap: 8,
  },
  badgeGroup: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
  },
  radioOption: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    padding: 12,
    borderWidth: StyleSheet.hairlineWidth,
    borderRadius: 8,
  },
  radio: {
    width: 20,
    height: 20,
    borderRadius: 10,
    borderWidth: 2,
    alignItems: "center",
    justifyContent: "center",
  },
  radioInner: {
    width: 10,
    height: 10,
    borderRadius: 5,
  },
  radioLabel: {
    flex: 1,
    gap: 4,
  },
  sourceLabel: {
    fontSize: 12,
    opacity: 0.6,
    textTransform: "uppercase",
  },
  sourceBadge: {
    padding: 12,
    borderWidth: StyleSheet.hairlineWidth,
    borderRadius: 8,
    gap: 4,
  },
  emptyState: {
    margin: 16,
    padding: 48,
    borderWidth: StyleSheet.hairlineWidth,
    borderRadius: 12,
    alignItems: "center",
    justifyContent: "center",
  },
});
